// en.ts — the dashboard's English copy, and the SHAPE of every other locale.
//
// Source of truth: `Catalog = typeof en` below is what `vi.ts` must satisfy, so a key added
// here without a Vietnamese counterpart fails `bun run check-types`. That compile error IS the
// mechanism (spec 2026-08-31 decision 5) — there is no runtime fallback, no missing-key
// warning, and deliberately no i18n library.
//
// Grouped by page/area, keys named for MEANING, never for their English text — a key called
// `noLeadsYet` is wrong the day the sentence changes. Values are strings, or functions taking a
// named object for interpolation; no template mini-language, no plural engine (Vietnamese has
// no plural inflection, and English plural branching stays in the calling code).
//
// What never lives here: the brand ("Embent", "Powered by Embent"), owner- or visitor-authored
// data, API enum values, and any plan fact — caps and prices derive from PLAN_LIMITS and are
// passed INTO the arg functions below (CLAUDE.md, "Pricing is rendered in THREE places").
export const en = {
  common: {
    close: 'Close',
  },
  /** `StatusChipState` (`@repo/ui`) labels. The English values ARE the enum strings — that is
   * what the chip's `{label ?? state}` fallback renders today, and extraction must not change a
   * rendered character. */
  status: {
    pending: 'pending',
    crawling: 'crawling',
    indexing: 'indexing',
    ready: 'ready',
    error: 'error',
    paused: 'paused',
    hint: 'hint',
    deleting: 'deleting',
  },
  /** `lib/relative-time.ts`'s vocabulary. Past and future are separate keys, not a sign flag:
   * Vietnamese puts the marker on the other side of the number ("3 ngày trước" / "sau 3 ngày"),
   * so one template with a swapped word would not survive translation. */
  time: {
    never: 'never',
    justNow: 'just now',
    inAMoment: 'in a moment',
    secondsAgo: ({ n }: { n: number }) => `${n}s ago`,
    inSeconds: ({ n }: { n: number }) => `in ${n}s`,
    minutesAgo: ({ n }: { n: number }) => `${n} min ago`,
    inMinutes: ({ n }: { n: number }) => `in ${n} min`,
    hoursAgo: ({ n }: { n: number }) => `${n}h ago`,
    inHours: ({ n }: { n: number }) => `in ${n}h`,
    daysAgo: ({ n }: { n: number }) => `${n}d ago`,
    inDays: ({ n }: { n: number }) => `in ${n}d`,
    monthsAgo: ({ n }: { n: number }) => `${n}mo ago`,
    inMonths: ({ n }: { n: number }) => `in ${n}mo`,
    yearsAgo: ({ n }: { n: number }) => `${n}y ago`,
    inYears: ({ n }: { n: number }) => `in ${n}y`,
  },
  /** The shell + layout batch (locale-dashboard plan, Task 4): the rail, mobile top bar/drawer,
   * admin layout's tab strip, the merged workspace+account menu, the usage/upgrade block, the
   * feedback dialog, and the usage-cap warning banner. A few entries are literal FRAGMENTS
   * (`themePrefix`/`languagePrefix`/`nextPrefix`, `accountLabelPrefix`) rather than whole
   * sentences — the markup they come from interleaves static text with a second dynamic value
   * inline (an icon, a nested `<span>`), so extracting the whole line into one arg function would
   * force the caller to also own that surrounding structure. */
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Viewing as ${email} — you are impersonating this user.`,
    stopImpersonating: 'Stop',

    platformAdmin: 'Platform Admin',
    adminTabWorkspaces: 'Workspaces',
    adminTabBots: 'Bots',
    adminTabJobs: 'Jobs',
    adminTabPosts: 'Posts',
    adminTabFeedback: 'Feedback',
    adminTabPlayground: 'Playground',

    /** Shared verbatim by `Rail.tsx`'s `RailError` and `MobileDrawer.tsx`'s `DrawerError` — the
     * same settled-but-failed-`useBots()` paragraph. `botsLoadErrorInline` is
     * `MobileTopBar.tsx`'s own shorter, period-less variant (inline text, not a paragraph) — a
     * different rendered string, so it gets its own key rather than reusing this one. */
    botsLoadError: "Couldn't load your bots.",
    botsLoadErrorInline: "Couldn't load your bots",
    reloadToRetry: 'Reload the page to try again.',

    navOverview: 'Overview',
    navUnanswered: 'Unanswered',
    navConversations: 'Conversations',
    navLeads: 'Leads',
    navSources: 'Sources',
    navPlayground: 'Playground',
    navAppearance: 'Appearance',
    navSettings: 'Settings',
    groupNeedsYou: 'NEEDS YOU',
    groupKnowledge: 'KNOWLEDGE',
    groupSetup: 'SETUP',

    closeMenu: 'Close menu',
    navigationLabel: 'Navigation',
    openNavigation: 'Open navigation',
    openAccountMenu: 'Open account menu',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, switch bot`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `YOUR BOTS${cap !== null ? ` · ${n} OF ${cap} USED` : ''}`,
    allBots: 'All bots',
    newBot: 'New bot',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `New bot — ${n} of ${cap} used`,

    pagesIndexed: 'Pages indexed',

    feedbackTrigger: 'Feedback',
    feedbackTypeGroupLabel: 'Feedback type',
    feedbackTypeBug: 'Bug',
    feedbackTypeFeature: 'Feature request',
    feedbackTypeQuestion: 'Question',
    feedbackPlaceholder: 'What happened, or what would make Embent better for you?',
    feedbackSent: 'Sent — we read every one.',
    feedbackSendError: 'Could not send feedback.',
    feedbackSending: 'Sending…',
    feedbackSend: 'Send',
    feedbackDialogTitle: 'Send feedback',
    feedbackDialogDescription: 'Bug, feature request, or question — this goes straight to the team.',

    workspaceSettings: 'Workspace settings',
    members: 'Members',
    billingUsage: 'Billing & usage',
    switchWorkspace: 'Switch workspace',
    accountLabelPrefix: 'Account — ',
    account: 'Account',
    signOut: 'Sign out',
    themePrefix: 'Theme: ',
    languagePrefix: 'Language: ',
    nextPrefix: 'next: ',
    themeSystem: 'System',
    themeDark: 'Dark',
    themeLight: 'Light',
    roleOwner: 'Owner',
    roleAdmin: 'Admin',
    roleMember: 'Member',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · you are ${role}` : `You are ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, workspace and account menu`,

    usageWarning: ({ pct }: { pct: number }) =>
      `You've used ${pct}% of your monthly messages — upgrade to keep every conversation flowing.`,
    dismiss: 'Dismiss',

    renewsOn: ({ date }: { date: string }) => `Renews ${date}`,
    resetsOn: ({ date }: { date: string }) => `Resets ${date}`,
    answersPaused: 'New answers paused — past 110% of cap',
    messagesLeft: ({ n }: { n: string }) => `${n} messages left this month`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} still shows the "Powered by Embent" badge`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} makes the "Powered by Embent" badge optional`,
    upgradeCta: ({ price }: { price: number }) => `Upgrade — $${price}/mo`,
    resumeAnswersCta: ({ price }: { price: number }) => `Resume answers — $${price}/mo`,
    resumeAnswersBenefit: 'Upgrading resumes answers immediately',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} adds ${messages} messages, ${bots} bots and ${seats} seat${seats === 1 ? '' : 's'}`,
  },
  /** `lib/bots-list.ts`'s chip/health-line vocabulary — extracted in full here (Task 4) even
   * though its first consumer, `Bots.tsx`, doesn't get its OWN copy extracted until Task 7 (it
   * only threads `t` through mechanically in this batch, per that function's now-required
   * parameter). `BotSwitcher.tsx` (this batch) is the real first consumer of these keys.
   *
   * Task 7 (Batch D) adds the second half below: the All-bots screen's own copy (`Bots.tsx`) —
   * column headers, empty state, the plan-cap upgrade gate, and the "New bot" dialog. The header
   * action's "New bot" button reuses `shell.newBot` rather than a duplicate key here — same
   * action (open the create-bot flow) as `BotSwitcher.tsx`'s own "New bot" row. */
  bots: {
    noSources: 'no sources',
    allSourcesReady: ({ n }: { n: number }) => `${n} · all ready`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'Paused',
    sourcesRemoving: ({ n }: { n: number }) => `${n} source${n === 1 ? '' : 's'} · removing`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} source${n === 1 ? '' : 's'} · sync error`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} source${n === 1 ? '' : 's'} · syncing`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} msg${messages === 1 ? '' : 's'} · ${unanswered} unanswered`,
    botsCount: ({ n }: { n: number }) => `${n} bot${n === 1 ? '' : 's'}`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n} of ${cap} used`,

    // Bots.tsx (Task 7)
    colBot: 'Bot',
    colStatus: 'Status',
    colMessages14d: 'Messages · 14d',
    colUnanswered: 'Unanswered',
    colSources: 'Sources',
    pageTitle: 'Bots',
    emptyTitle: 'Create your first bot',
    emptyDescription:
      'Point it at your website or docs — it crawls the content and starts answering visitor questions.',
    planIncludesBots: ({ cap }: { cap: number }) => `Your plan includes ${cap} bot${cap === 1 ? '' : 's'}`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} ($${priceUsd}/mo) runs up to ${maxBots} bots — one per site, each with its own knowledge and look.`,
    comparePlansLink: 'Compare plans →',
    newBotDialogTitle: 'New bot',
    botNameFieldLabel: 'Name',
    botNamePlaceholder: 'Acme Support Bot',
    creatingCta: 'Creating…',
    createCta: 'Create',
  },
  /** Locale-dashboard plan, Task 5: sign-in/up, verification, password reset, invite-accept,
   * unsubscribe, the catch-all, and the "/" resolver's error state — grouped here per the plan's
   * batch, not because every page below is literally an auth screen (`NotFound`/`LandingResolver`
   * aren't). A handful of keys are reused verbatim across two pages where the meaning really is
   * the same field/action (`emailLabel`, `passwordLabel`, `continueWithGoogle`, `backToSignIn`,
   * `genericFormError`) — everywhere else, a matching English string still gets its OWN key
   * (`signInTitle` vs `signInCta` vs `inviteSignInCta` all render "Sign in" today) because the
   * roles are different (heading vs submit button vs a link on a different page) and nothing
   * guarantees they stay in sync. `error.message`/`ApiError.message` passthroughs from
   * better-auth or our own API are NEVER captured here (server-authored, out of scope per the
   * plan) — only the friendly fallback strings THIS app substitutes when there is no server
   * message to show. */
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Password',
    genericFormError: 'Something went wrong. Please try again.',
    continueWithGoogle: 'Continue with Google',
    backToSignIn: 'Back to sign in',

    // SignIn.tsx
    signInTitle: 'Sign in',
    signInSubtitle: 'Welcome back to Embent.',
    passwordJustResetNotice: 'Password updated — sign in with your new password.',
    forgotPasswordLink: 'Forgot password?',
    unverifiedResendNotice: "Check your inbox — we've re-sent the verification email.",
    signingIn: 'Signing in…',
    signInCta: 'Sign in',
    noAccountPrompt: "Don't have an account? ",
    signUpLink: 'Sign up',

    // SignUp.tsx
    signUpTitle: 'Create your account',
    signUpSubtitle: 'Start answering visitor questions in minutes.',
    nameLabel: 'Name',
    creatingAccount: 'Creating account…',
    createAccountCta: 'Create account',
    haveAccountPrompt: 'Already have an account? ',
    signInLink: 'Sign in',

    // CheckEmail.tsx
    checkEmailTitle: 'Check your email',
    checkEmailSubtitle: 'We sent you a verification link. It usually arrives within a minute.',
    checkEmailBody: 'Click the link in the email to activate your account.',

    // VerifyEmail.tsx
    verifyFailedTitle: 'Verification failed',
    verifiedTitle: 'Email verified',
    verifyFailedBody: 'That verification link is invalid or has expired.',
    verifiedBody: "You're all set — taking you to your dashboard…",

    // RequestReset.tsx
    requestResetTitle: 'Reset your password',
    requestResetSubtitlePending: "Enter your email and we'll send you a reset link.",
    requestResetSubtitleSent: "If that email has an Embent account, we've sent a reset link.",
    requestResetBodySent: 'Check your inbox. The link expires in 1 hour.',
    sendingResetLink: 'Sending…',
    sendResetLinkCta: 'Send reset link',

    // ResetPassword.tsx
    resetLinkInvalidTitle: 'Reset link invalid',
    resetLinkInvalidBody: 'This password reset link is missing its token, or has already been used.',
    requestNewLinkCta: 'Request a new link',
    chooseNewPasswordTitle: 'Choose a new password',
    chooseNewPasswordSubtitle: "Make it something you haven't used before.",
    newPasswordLabel: 'New password',
    resetLinkExpiredFallback: 'That reset link is invalid or has expired.',
    savingPassword: 'Saving…',
    saveNewPasswordCta: 'Save new password',

    // InviteAccept.tsx
    inviteNotValidTitle: 'Invite not valid',
    goToEmbentLink: 'Go to Embent',
    inviteInvalidFallback: 'This invite is no longer valid.',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `You're invited to ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) =>
      `Sent to ${email} — sign in or create an account with that email to accept.`,
    inviteSignInCta: 'Sign in',
    inviteSignUpCta: 'Create an account',
    wrongAccountTitle: 'Wrong account',
    signOutCta: 'Sign out',
    inviteAcceptFallback: 'Something went wrong accepting this invite.',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Joining ${workspaceName}…`,
    joiningWorkspaceBody: 'Just a moment.',

    // Unsubscribe.tsx — `eventLabels` keyed by `NotificationEvent` (`@repo/shared-types`); the
    // English values ARE the lowercase noun phrases `unsubscribedBody` names inline.
    eventLabels: {
      newLead: 'new lead',
      unansweredDigest: 'unanswered digest',
      crawlFailed: 'crawl failed',
      weeklySummary: 'weekly summary',
    },
    unsubscribeLinkExpiredTitle: 'This link has expired',
    unsubscribeLinkExpiredBody: "Manage notifications from the bot's settings in the dashboard.",
    openDashboardCta: 'Open dashboard',
    unsubscribedTitle: "You're unsubscribed",
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `You won't get ${eventLabel} emails for ${botName ?? 'this bot'} anymore. You can turn them back on in the bot's settings.`,

    // NotFound.tsx
    notFoundEyebrow: 'Page not found',
    notFoundTitle: 'Nothing lives at this address',
    notFoundBody: 'The link may be out of date, or the page may have moved.',
    notFoundCta: 'Go to your bots',

    // LandingResolver.tsx
    landingBotsErrorFallback: 'Something went wrong loading your bots.',
    landingGoToBotsCta: 'Go to your bots →',
  },
  /** Locale-dashboard plan, Task 6 (Batch C): the onboarding wizard — `Onboarding.tsx`'s own
   * "Skip for now" link, `Stepper`, and the five step components (Website/Crawl/Try it/Look/Go
   * live). A few strings are shared verbatim across steps because they really are the same
   * field or role, not by coincidence — `websiteUrlLabel` (`WebsiteStep`'s URL field and
   * `CrawlStep`'s replace-URL field are both "the website URL" input) and `invalidWebsiteUrl`
   * (both call the same `normalizeUrl` and show the identical failure text). `genericFallbackError`
   * is likewise shared between `WebsiteStep` and `TryItStep`'s own non-`Error` catch branches.
   * Everywhere `err.message` itself is shown directly (`LookStep`'s save error, `TryItStep`'s
   * streamed SSE `error` frame) that is a server/API passthrough and stays OUT of the catalog
   * per the plan — only the friendly fallback literals this app substitutes get a key. The
   * go-live snippet itself (rendered by `EmbedSnippet`) is code, not copy, and isn't touched
   * here. */
  onboarding: {
    skipForNow: 'Skip for now',
    invalidWebsiteUrl: 'Enter a valid website URL, e.g. https://acme.vn',
    websiteUrlLabel: 'Website URL',
    genericFallbackError: 'Something went wrong.',

    // Onboarding.tsx
    /** `CrawlStep`'s `hostname` prop fallback while `source`/`bot` are still in flight (or a
     * bot somehow has neither a name nor a source yet) — reachable, not dead code, so it needs
     * a key like everything else on screen. */
    crawlDefaultHostname: 'your site',
    /** Thrown client-side when `replaceUrlMutation` fires with no bot/source to replace yet;
     * caught by its own `onError`, which sets `replaceUrlError`, which `CrawlStep` renders
     * verbatim — a real rendered string, not a passthrough of a server message. */
    noSourceToReplace: 'No source to replace yet.',

    // Stepper.tsx
    stepWebsite: 'Website',
    stepCrawl: 'Crawl',
    stepTryIt: 'Try it',
    stepLook: 'Look',
    stepGoLive: 'Go live',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Step ${current} of ${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Onboarding progress: step ${current} of ${total}, ${label}`,
    stepTablistAriaLabel: 'Onboarding progress',

    // WebsiteStep.tsx
    websiteHeading: 'Add your website',
    websiteSubtitle:
      'Embent reads your site and builds a bot that answers with citations — nothing made up. Most sites are ready in a few minutes.',
    botNameLabel: 'Bot name',
    botNamePlaceholder: 'Acme Support',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: "We'll find your pages automatically. You can add more sources later.",
    botNameRequired: 'Enter a name for your bot.',
    websiteStarting: 'Starting…',
    websiteSubmitCta: 'Start reading my site',

    // CrawlStep.tsx
    crawlHeading: ({ hostname }: { hostname: string }) => `Reading ${hostname}…`,
    crawlSubtitleReady: 'All done — every page is indexed and ready to answer.',
    crawlSubtitleSyncing: "You don't have to wait here — try your bot while it finishes.",
    /** The rotating phase-truthful status line's vocabulary (prod-copy-fixes cycle, defect 2) —
     * kept as a `Record<SyncingStatus, string[]>` here, same shape as the module-level constant
     * it replaces, so `CrawlStep.tsx` still just indexes by phase. */
    crawlStatusLines: {
      pending: ['Queued — starting shortly…', 'Getting ready to start…'],
      crawling: ['Finding pages on the site…', 'Following links…', 'Reading pages…'],
      indexing: ['Building the search index…', 'Processing what was read…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} pages indexed`,
    crawlNoSourceYet: "We couldn't find a source for this bot yet.",
    crawlErrorLead:
      'The most likely cause is a typo in the address — double-check it below and try again.',
    crawlReplaceUrlCta: 'Try a different address',
    crawlReplaceUrlUpdating: 'Updating…',
    crawlReplaceUrlHelper: "We'll remove the failed attempt and start fresh with this address.",
    crawlRetryCta: 'Retry',
    crawlRetrying: 'Retrying…',
    crawlRetryHelper:
      'If the address is right and the site was just briefly unreachable, retry instead. Sites ' +
      'that need JavaScript to show their content can be handled with a setting available after ' +
      'setup, on the Sources screen.',
    crawlCtaSyncing: 'Try your bot while it finishes →',
    crawlCtaReady: 'Next: try your bot →',

    // TryItStep.tsx
    tryItHeading: 'Ask it anything',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `It already knows ${n} page${n === 1 ? '' : 's'} — and it's still learning the rest. Every answer shows its sources.`,
    tryItSubtitleNoPages:
      "It's still reading your site — try a question anyway; answers only get better from here.",
    tryItSamplePrompt: `Try: "What's your return policy?"`,
    tryItPlaceholder: 'Ask about shipping, pricing, anything…',
    tryItSending: 'Sending…',
    tryItSendCta: 'Send',
    tryItNextCta: 'Looks good — make it mine',

    // LookStep.tsx
    lookHeading: 'Make it match your site',
    lookSubtitle:
      'Pick your brand color — the preview updates as you type. You can fine-tune everything later in Appearance.',
    brandColorLabel: 'Brand color',
    colorHexAriaLabel: 'Color hex value',
    welcomeMessageLabel: 'Welcome message',
    lookSaving: 'Saving…',
    lookContinueCta: 'Continue',

    // GoLiveStep.tsx
    goLiveHeading: "One snippet and you're live",
    // Split around the `<code>&lt;/body&gt;</code>` element the JSX renders inline — the tag
    // itself is code, not copy, and stays in the component untouched.
    goLivePasteBeforePrefix: 'Paste this before the closing',
    goLivePasteBeforeSuffix: "tag on your site. That's it — the bot appears bottom-right.",
    goLiveInstallGuidesLink: 'Platform install guides',
    goLiveFirstAnswerNotice: "We'll email you the moment the widget answers its first visitor.",
    goLiveDashboardCta: 'Go to dashboard',
    goLiveViewSourcesCta: 'View sources',
  },
  /** Locale-dashboard plan, Task 7 (Batch D): the Bot Overview screen in both its states —
   * `BotOverview.tsx` (populated, embeds `NeedsYou.tsx`) and `FirstRun.tsx` (brand-new, the same
   * screen before the bot has ever held a conversation) — plus `lib/overview.ts`'s
   * `knowledgeChipView`, whose `t: Catalog` parameter landed in Task 3 already required, ahead of
   * these four labels (`knowledgeAllReady`/`knowledgeSyncing`/`knowledgeNeedsAttention`/
   * `knowledgeRemoving`) actually moving here.
   *
   * The two screens render byte-identical Knowledge cards, so their shared rows
   * (`knowledgeHeading`, `pagesIndexed`, `sourcesCountLabel`, `lastSyncedLabel`, `pointAtSite`,
   * `addSourceLink`) get ONE key apiece rather than one per file. `installGuidesLink` text is
   * NOT duplicated here — `FirstRun.tsx` reuses `onboarding.goLiveInstallGuidesLink` verbatim,
   * same link, same destination, same meaning as the onboarding wizard's Go-live step.
   *
   * `emailSnippetSubject`/`emailSnippetBody` back the mailto link `FirstRun.tsx` builds through
   * `lib/embed-snippet.ts`'s new `mailtoSnippetLink` — moved there (not left inline in the
   * component) because that file is already the one place the widget's `<script>` tag itself is
   * built, and the mailto body embeds that exact snippet. */
  overview: {
    title: 'Overview',
    liveOnPrefix: 'Live on ',
    answeringSince: ({ date }: { date: string }) => `answering since ${date}`,
    testInPlaygroundCta: 'Test in Playground',
    addSourcesCta: 'Add sources',
    botDetailsLoadError: ({ message }: { message: string }) => `Couldn't load bot details — ${message}`,

    messagesLabel: 'Messages · last 14 days',
    conversationsLink: 'Conversations →',
    vsPreviousPeriod: 'vs previous 14 days',
    leadsLabel: 'Leads captured',
    newLeadsCount: ({ n }: { n: number }) => `${n} new`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} this week`,
    viewLeadsLink: 'View leads →',
    unansweredLabel: 'Unanswered questions',
    fixThemLink: 'Fix them →',

    knowledgeHeading: 'Knowledge',
    pointAtSite: 'Point it at your site',
    addSourceLink: 'Add a source →',
    addSourcesLink: 'Add sources →',
    pagesIndexed: 'Pages indexed',
    sourcesCountLabel: 'Sources',
    lastSyncedLabel: 'Last synced',
    manageSourcesLink: 'Manage sources →',
    resyncing: 'Re-syncing…',
    allSyncing: 'All syncing',
    resyncCta: 'Re-sync',
    resyncNothingToSync: 'Nothing to sync — every source is already syncing.',
    resyncGenericError: "Couldn't start a re-sync — please try again.",

    latestConversationsHeading: 'Latest conversations',
    allConversationsLink: 'All conversations →',
    noConversationsEmpty: 'Quiet in the last 14 days — new conversations will show up here.',
    noMessageFallback: '(no message)',
    leadCapturedTag: 'Lead captured',
    hasUnansweredTag: 'Has unanswered',

    // lib/overview.ts's `knowledgeChipView`
    knowledgeAllReady: 'All ready',
    knowledgeSyncing: 'Sources syncing',
    knowledgeNeedsAttention: 'A source needs attention',
    knowledgeRemoving: 'Removing sources',

    // FirstRun.tsx
    createdSubtitle: ({ time }: { time: string }) => `Created ${time}`,
    zeroStatMessagesHint: 'Starts on the first question',
    zeroStatLeadsHint: 'Name and email, mid-chat',
    zeroStatUnansweredHint: 'Nothing to fix yet',
    heroReadyHeadline: 'Your bot is ready to answer',
    heroIndexingHeadline: 'Still reading your site',
    heroEmptyHeadline: 'Add a source to get started',
    heroEmptyBody:
      "It doesn't have anything to read yet. Add a source and it'll be ready to answer within minutes.",
    heroIndexingErrorBody:
      "A source needs attention — it won't finish on its own. Check the Knowledge card to see what's wrong, then put it on your site once it's fixed.",
    heroIndexingPrefix: "It's still reading",
    heroIndexingNoSourceFallback: 'your source',
    heroIndexingSuffix: ' — check back in a few minutes, then put it on your site.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `It read ${n} ${n === 1 ? 'page' : 'pages'}`,
    heroReadyFromDomain: ' from ',
    heroReadyBodySuffix:
      ' and knows them well enough to answer questions about them. One step left: put it on your site.',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `Only ${n} ${n === 1 ? 'page' : 'pages'}? Add your FAQ, pricing or policy pages so it has more to work with.`,
    addSourceCta: 'Add a source',
    addSourceHelperText:
      'Takes about a minute. The embed snippet will be waiting right here once it has something to answer from.',
    pasteBeforeBodyLabel: 'PASTE THIS BEFORE </BODY>',
    emailSnippetLink: 'Email the snippet to my developer',
    emailSnippetSubject: ({ botName }: { botName: string }) => `Embed ${botName} on our site`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `Paste this before the closing </body> tag:\n\n${snippet}`,
    whatHappensNextHeading: 'What happens next',
    stepVisitorAsksTitle: 'A visitor asks something',
    stepVisitorAsksBody: 'The bubble sits bottom-right of every page the snippet is on. Nothing else to set up.',
    stepEmailAnswerTitle: 'We email you the first answer',
    stepEmailAnswerBody: "So you read exactly what it said, in your customer's words, before anyone else does.",
    stepUnansweredTitle: "What it couldn't answer lands here",
    stepUnansweredBody: 'It refuses rather than invents. Each refusal becomes one line for you to fix.',
    stepAddSourceTitle: 'Add a source',
    stepAddSourceBody:
      'Point it at your site, or upload files and pasted text — it needs something to read before it can answer.',
    stepAddSourceVisitorTitle: 'A visitor asks, you get emailed the answer',
    countersHeading: 'COUNTERS START WITH YOUR FIRST VISITOR',

    // NeedsYou.tsx
    needsYouHeading: 'Needs you',
    allUnansweredLink: 'All unanswered →',
    needsYouEmpty: 'Nothing to fix — your bot has answered everything it was asked.',
    needsYouIntro: "Your bot said it didn't know. Answer once and it knows forever.",
    askedSummary: ({ n, time }: { n: number; time: string }) =>
      `${n === 1 ? 'Asked once' : `Asked ${n} times`} · last ${time}`,
    writeAnswerCta: 'Write answer',
    cancelCta: 'Cancel',
    answerPlaceholder: "Write the answer you'd give this visitor. The bot will use it from now on.",
    addingCta: 'Adding…',
    addAsQaSourceCta: 'Add as Q&A source',
    saveAnswerError: "Couldn't save that answer — please try again.",
  },
  /** Locale-dashboard plan, Task 8 (Batch E): the sources list screen (`BotSources.tsx`) — the
   * add-source tabs (URL/sitemap/text/file), the table (and its own loading skeleton, which
   * renders the literal same header row and so shares these `col*` keys), per-row actions, the
   * delete-confirmation dialog, and the near-empty-pages info popover. Also carries
   * `RenderedModeToggle.tsx`'s `RenderedModeExplainer` popover and the rendered-mode switch's own
   * confirm dialog — shared by `BotSources.tsx` here and, ahead of Task 9, `SourceDetail.tsx`
   * (whose own two call sites now pass `t` as a mechanical signature follow, same "thread ahead
   * of the batch that owns the screen" precedent Task 4 set for `lib/overview.ts`'s
   * `knowledgeChipView` — `SourceDetail.tsx`'s OWN copy, e.g. its neighboring "Rendered mode"
   * label, stays untouched here, Task 9's job).
   *
   * `deleteConfirmDescriptionPrefix`/`Suffix` split around the bolded source URI the JSX renders
   * inline, same fragment idiom as `shell.themePrefix` — the URI itself is owner data, not copy.
   * `lib/source-overrides.ts` carries no copy at all (pure status-shape logic over `SourceDto`)
   * and stays untouched; same for `lib/normalize-url.ts`, which has no user-facing message today
   * (it only ever returns the normalized string or `null`) — nothing there to extract. */
  sources: {
    pageTitle: 'Sources',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) =>
      `${n} source${n === 1 ? '' : 's'} · ${pageCount} page${pageCount === 1 ? '' : 's'} · last synced ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `Couldn't load this bot — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Text',
    typeFile: 'File',

    colType: 'Type',
    colUri: 'URI',
    colStatus: 'Status',
    colRenderedMode: 'Rendered mode',
    colPages: 'Pages',
    colLastSynced: 'Last synced',
    colActions: 'Actions',

    urlFieldLabel: 'Page URL',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'Sitemap URL',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Title',
    textTitlePlaceholder: 'Refund policy',
    textContentFieldLabel: 'Content',
    textContentPlaceholder: 'Paste the text you want the bot to know about…',
    fileFieldLabel: 'File',
    fileDropPrompt: 'Drop a file here, or click to browse',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · up to ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `That file is larger than the ${maxSize} limit.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'That file'} isn't supported — use ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `Using "${name}" — ${count} other file${count === 1 ? '' : 's'} dropped, ignored.`,
    fileDropNotAFile: "That wasn't a file — drag a file from your computer instead.",
    fileTitleFieldLabel: 'Title (optional)',
    fileTitlePlaceholder: 'Defaults to the file name',

    addingCta: 'Adding…',
    addSourceCta: 'Add source',

    emptyTitle: 'Point it at your site',
    emptyDescription: "Add a URL, sitemap, text, or file above and we'll start reading it.",

    reviewPagesLink: 'Review pages',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) =>
      `${count} page${count === 1 ? '' : 's'} looked empty`,

    resyncCta: 'Resync',
    deleteCta: 'Delete',
    cancelCta: 'Cancel',
    syncInProgressError: 'Sync in progress — try again later',

    deleteConfirmTitle: 'Delete this source?',
    deleteConfirmDescriptionPrefix: 'Removes ',
    deleteConfirmDescriptionSuffix: ' and everything it indexed. This cannot be undone.',

    // RenderedModeToggle.tsx
    renderedModeExplainerAriaLabel: 'What is rendered mode?',
    renderedModeExplainerBody1Bold: 'Rendered mode',
    renderedModeExplainerBody1Rest:
      ' runs each page in a real browser before reading it, so content that only appears after JavaScript runs is captured.',
    renderedModeExplainerBody2:
      "Leave it off for normal sites — it's faster and cheaper. Turn it on when a page looks empty to the crawler (single-page apps, dashboards, anything that loads its text after the page opens).",
    renderedModeExplainerBody3:
      'Trade-off: crawling is slower and uses more of your page budget per run.',

    renderedModeLabel: 'Rendered mode',
    modeRendered: 'Rendered',
    modeStandard: 'Standard',
    switchModeTitle: ({ mode }: { mode: string }) => `Switch to ${mode} mode?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `This re-crawls the source in ${mode} mode. Pages that come back different are re-indexed and any that no longer exist are removed — the current index stays available until the new crawl finishes.`,
    switchModeCta: ({ mode }: { mode: string }) => `Switch to ${mode} mode`,
  },
  /** Locale-dashboard plan, Task 9 (Batch F): the source detail screen (`SourceDetail.tsx`) and
   * `lib/crawl-run.ts`'s `describeCrawlRun` — the owner-facing paragraph rendered above the page
   * list for one `CrawlRun` row. Mints its own keys throughout rather than reusing `sources.*`'s
   * coincidentally-identical words (`typeUrl`/`colRenderedMode`/`cancelCta`, …) — same policy the
   * `auth` group states explicitly: a matching English string still gets its OWN key when the
   * role is different (a facts-card label here vs. a table column there), because nothing
   * guarantees the two stay in sync. The two actual exceptions are `RenderedModeToggle`/
   * `RenderedModeExplainer` themselves — the literal SAME shared component, not just matching
   * text — which already pull their own copy from `t.sources.*` since Task 8's mechanical thread.
   *
   * `error.message`/`ApiError.message` passthroughs (the content viewer's fetch error, the action
   * error banner, the page-load error) stay OUT of the catalog, same as every other batch — only
   * the friendly literals this app substitutes get a key. `indexingFailureMessage`
   * (`@repo/shared-types`) also stays untouched here: it's shared, byte-for-byte, with
   * `verify_indexing`'s own hint text written on the server, and duplicating it into a per-app
   * catalog would break that parity. Raw CF codes (`doc.indexError`) and the bare `'—'`
   * empty-value placeholder are data/punctuation, not copy — same treatment `Bots.tsx`'s own
   * `'—'` already gets.
   *
   * `describeCrawlRun`'s six `CrawlOutcome` branches (see that file's own header comment for why
   * `outcome`, never `error`, drives every sentence below) map to `crawledPages`/
   * `crawledPagesWithSkipped`/`truncatedCapacity`/`truncatedDeadline`/`truncatedAttempts`/
   * `truncatedPageCap`/`crawlFailedGeneric`; `remainingAtLeast`/`remainingNone` back the shared
   * `remainingClause` helper the three truncation branches all call through. English's plural
   * branching for "page(s)" lives inside each arg function itself (CLAUDE.md: Vietnamese has no
   * plural inflection), never in the calling code — matching every other `n === 1 ? '' : 's'`
   * key already in this file. */
  sourceDetail: {
    // lib/crawl-run.ts — describeCrawlRun
    crawledPages: ({ n }: { n: number }) => `Crawled ${n} page${n === 1 ? '' : 's'}.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `Crawled ${n} page${n === 1 ? '' : 's'}. ${failedCount} page${failedCount === 1 ? '' : 's'} didn't load and ${failedCount === 1 ? 'was' : 'were'} skipped.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `Crawled ${n} page${n === 1 ? '' : 's'} before our own crawling capacity ran out. This is temporary on our side — wait a few minutes and re-run the crawl.`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `Crawled ${n} page${n === 1 ? '' : 's'} before this crawl hit our own time limit.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `Crawled ${n} page${n === 1 ? '' : 's'} before the crawl ran out of attempts on pages that wouldn't load.`,
    truncatedPageCap: ({ n }: { n: number }) =>
      `Crawled ${n} page${n === 1 ? '' : 's'} — the most your plan's page cap allows right now.`,
    remainingAtLeast: ({ n }: { n: number }) =>
      ` At least ${n} page${n === 1 ? '' : 's'} weren't indexed.`,
    remainingNone: ' Some pages may not have been indexed.',
    crawlFailedGeneric: "This crawl didn't index any pages.",

    // SourceDetail.tsx — typeLabel
    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Text',
    typeFile: 'File',

    // content-language design (2026-08-22, THI-119) Task 2 — the header's "Language: …" line
    languagePrefix: 'Language: ',

    // Facts card
    factType: 'Type',
    factRenderedMode: 'Rendered mode',
    factLastSynced: 'Last synced',

    resyncCta: 'Resync',
    deleteCta: 'Delete',

    latestCrawlHeading: 'Latest crawl',
    neverCrawledYet: "This source hasn't been crawled yet.",
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `This crawl ran in ${rendered ? 'rendered' : 'standard'} mode.`,

    // CrawlDiagnostics
    failedUrlsHeading: 'Failed URLs',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} total, showing the first ${shown}`,
    offSiteRedirectsHeading: 'Off-site redirects',
    offSiteRedirectsMoreCaveat: ' — there may be more than this list shows',
    nearEmptyCameBack: ({ n }: { n: number }) =>
      `${n} page${n === 1 ? '' : 's'} came back with almost no content.`,

    pagesIndexedHeading: 'Pages indexed',
    nearEmptyOnlyToggle: 'Near-empty only',

    // DocumentsTable
    noPagesIndexedYet: 'No pages indexed yet.',
    colUrl: 'URL',
    colCharacters: 'Characters',
    colStatus: 'Status',
    nearEmptyBadge: 'near-empty',

    // indexStateLabel
    indexStateIndexed: 'Indexed',
    indexStateIndexing: 'Indexing…',
    indexStateFailed: 'Failed',

    // DocumentContentViewer
    fileNoMarkdownExplainer:
      'Stored as the uploaded file itself — we keep the original, not a separate text copy, so there is nothing to preview here. The file is indexed and the bot answers from it.',
    nothingStoredForDocument: 'Nothing stored for this document.',

    storedContentHeading: 'Stored content',
    nothingStoredYet: 'Nothing stored yet.',

    deleteDialogTitle: 'Delete this source?',
    deleteDialogDescriptionPrefix: 'Removes ',
    deleteDialogDescriptionSuffix: ' and everything it indexed. This cannot be undone.',
    cancelCta: 'Cancel',
  },
  /** Locale-dashboard plan, Task 10 (Batch G): the Conversations screen (`BotConversations.tsx`)
   * — the date-grouped list + transcript pane, its two delete-confirmation dialogs (one
   * conversation, "delete all"), the header, and the empty state. Also carries
   * `components/chat-bubbles.tsx`'s two `StatusChip` labels: that file has no screen of its
   * own, and this is the group whose screen (the transcript viewer) consumes it, so
   * `unansweredChipLabel` lives here and the chip's "Lead captured" label reuses
   * `leadCapturedTag` below rather than minting a byte-identical twin. Playground.tsx's OWN
   * use of the same shared component (Task 11) is untouched by that reuse — its dashboard
   * chrome is that task's job, and the component itself doesn't change here, only its source
   * of these two strings does.
   *
   * `compactRelativeTime` — the list row's own "12m"/"1h"/"1d" timestamp, a LOCAL copy of
   * `lib/overview.ts`'s same-named, same-shaped helper — is NOT extracted here: that file
   * already hardcodes the identical 'now'/m/h/d vocabulary and was deliberately left as a
   * flagged gap in Task 7 ("localization gap for the translator/final review to weigh");
   * `lib/overview.ts` isn't in this batch's file list, and fixing only this file's copy while
   * leaving the other would just relocate the inconsistency rather than resolve it.
   *
   * `deleteAllCta` is one key for BOTH the list header's button and the confirm dialog's own
   * primary button — literally the same rendered word in the same flow, same idiom as
   * `deletingCta`/`cancelCta` reused across both dialogs on this screen. */
  conversations: {
    today: 'Today',
    yesterday: 'Yesterday',
    yourTesting: 'Your testing',
    visitor: 'Visitor',
    playgroundTag: 'Playground',
    noMessageFallback: '(no message)',
    leadCapturedTag: 'Lead captured',
    hasUnansweredTag: 'Has unanswered',
    unansweredChipLabel: 'Unanswered',
    loadingMore: 'Loading more…',
    loadMore: 'Load more',
    selectConversationPrompt: 'Select a conversation to view its transcript.',
    backToConversations: '← Back to conversations',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) =>
      `Started ${time} · ${n} messages`,
    deleteConversationAriaLabel: 'Delete this conversation',
    leadSubmittedTag: '✓ Lead submitted',
    cancelCta: 'Cancel',
    deletingCta: 'Deleting…',

    pageTitle: 'Conversations',
    headerSubtitle: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'conversation' : 'conversations'} · newest first`,
    emptyTitle: 'Waiting for your first visitor',
    emptyDescription: 'Conversations show up here as soon as someone chats with your bot.',

    deleteAllCta: 'Delete all conversations',
    deleteDialogTitle: 'Delete this conversation?',
    deleteDialogDescription:
      'Permanently deletes this transcript and its messages. A lead captured in it stays on the Leads tab. This cannot be undone.',
    deleteCta: 'Delete conversation',

    deleteAllDialogTitle: 'Delete all conversations?',
    deleteAllDescriptionPrefix:
      'Permanently deletes every conversation and message for this bot. Captured leads stay on the Leads tab. This cannot be undone. Type ',
    deleteAllDescriptionSuffix: ' to confirm.',
    confirmBotNameAriaLabel: 'Confirm bot name',
  },
  /** Locale-dashboard plan, Task 10 (Batch G): the Leads screen (`BotLeads.tsx`) — the
   * triage-state filter tabs, the table (whose header now says "Contact", a single stacked
   * email+phone cell, not "Email" — 2026-08-22 lead-phone-capture design), row actions, the
   * bulk bar, CSV export chrome, and both delete-confirmation dialogs.
   *
   * `actionMarkNew`/`actionMarkContacted`/`actionArchive` back BOTH the per-row status actions
   * AND the bulk-bar's own "Mark contacted"/"Archive" buttons — same action, same word, just
   * triggered from two different controls on the SAME screen, not two independent features
   * that could plausibly drift apart the way `auth`'s cross-screen "Sign in" pairs can.
   * `statusNew`/`statusContacted`/`statusArchived` are the raw status badge's own text — today
   * IDENTICAL to the filter labels lowercased, but kept as separate keys since the badge and
   * the filter tab are different roles (a per-row fact vs. a screen-wide toggle).
   *
   * `noContactFallback` ('this lead') is the delete DIALOG's own fallback when a lead has
   * neither email nor phone — deliberately a different string than `noEmailFallback`
   * ('(no email)', the checkbox/delete-button aria-labels' fallback), per the lead-phone-capture
   * fix this task's brief called out by name. */
  leads: {
    filterNew: 'New',
    filterContacted: 'Contacted',
    filterArchived: 'Archived',
    filterAll: 'All',
    actionMarkNew: 'Mark new',
    actionMarkContacted: 'Mark contacted',
    actionArchive: 'Archive',
    statusNew: 'new',
    statusContacted: 'contacted',
    statusArchived: 'archived',

    filterByStatusAriaLabel: 'Filter by status',
    pageTitle: 'Leads',
    exportingCta: 'Exporting…',
    exportCsvCta: 'Export CSV',
    exportCsvError: 'Could not export leads — please try again.',

    headerSubtitleAll: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'lead' : 'leads'} captured · newest first`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} ${filterLabel} ${n === 1 ? 'lead' : 'leads'} · newest first`,

    deleteAllCta: 'Delete all leads',
    bulkActionsAriaLabel: 'Bulk actions',
    selectedCount: ({ n }: { n: number }) => `${n} selected`,
    bulkCapMessage: ({ max }: { max: number }) => `Select up to ${max} leads at a time`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n} updated`,
    bulkUpdateError: "Couldn't update those leads. Try again.",

    emptyTitle: 'Waiting for your first lead',
    emptyDescription: "Leads captured by your bot's chat widget will show up here.",
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `No ${filterLabel} leads`,
    noFilterResultsDescription: 'Nothing in this filter right now.',

    selectAllAriaLabel: 'Select all leads',
    colName: 'Name',
    colContact: 'Contact',
    colIntent: 'Intent',
    colStatus: 'Status',
    colCaptured: 'Captured',
    colActions: 'Actions',

    noEmailFallback: '(no email)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Select lead ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Delete lead ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: 'View chat',
    loadingMoreCta: 'Loading more…',
    loadMoreCta: 'Load more',

    cancelCta: 'Cancel',
    deletingCta: 'Deleting…',

    deleteDialogTitle: 'Delete this lead?',
    deleteDescriptionPrefix: 'Permanently deletes ',
    deleteDescriptionSuffix: '. This cannot be undone.',
    noContactFallback: 'this lead',
    deleteCta: 'Delete lead',

    deleteAllDialogTitle: 'Delete all leads?',
    deleteAllDescriptionPrefix:
      'Permanently deletes every lead captured for this bot. This cannot be undone. Type ',
    deleteAllDescriptionSuffix: ' to confirm.',
    confirmBotNameAriaLabel: 'Confirm bot name',
  },
  /** Locale-dashboard plan, Task 10 (Batch G): the Unanswered screen (`BotUnanswered.tsx`) — the
   * Open/Dismissed/Answered segmented tabs, each tab's own empty state, the inline "Write
   * answer" editor, and the Answered tab's read-only rows.
   *
   * `viewChatCta`/`answerPlaceholder`/`addingCta`/`addAsQaSourceCta`/`saveAnswerError` are
   * byte-identical to `overview.viewLeadsLink`-style near-matches already in the catalog
   * (`overview.answerPlaceholder`, `overview.addingCta`, `overview.addAsQaSourceCta`,
   * `overview.saveAnswerError` back `NeedsYou.tsx`'s OWN copy of this same "write an answer"
   * pattern) — minted as separate keys here rather than reused, same policy as `auth`'s
   * cross-screen "Sign in" pairs: two independent implementations of the same feature on two
   * different screens, nothing guarantees they stay in sync just because they say the same
   * thing today. */
  unanswered: {
    tabOpen: 'Open',
    tabDismissed: 'Dismissed',
    tabAnswered: 'Answered',
    filterByStatusAriaLabel: 'Filter by status',

    pageTitle: 'Unanswered',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) =>
      `${n} question${n === 1 ? '' : 's'} ${verb}`,
    subtitleVerbOpen: 'waiting for an answer',
    subtitleVerbDismissed: 'dismissed',
    subtitleVerbAnswered: 'answered',

    openTabIntro:
      "Questions your bot couldn't answer, grouped. Write an answer once — it becomes a Q&A source and the bot knows it forever.",

    emptyTitleOpen: "Nothing open — your bot answered everything it was asked.",
    emptyTitleDismissed: 'Nothing dismissed.',
    emptyTitleAnswered: 'No answered questions yet.',

    askedOnce: 'Asked once',
    askedTimes: ({ n }: { n: number }) => `Asked ${n} times`,

    viewSourceCta: 'View source',
    sourceRemoved: 'source removed',
    viewChatCta: 'View chat',
    chatDeleted: 'chat deleted',

    dismissCta: 'Dismiss',
    reopenCta: 'Reopen',
    writeAnswerCta: 'Write answer',
    cancelCta: 'Cancel',
    answerPlaceholder: "Write the answer you'd give this visitor. The bot will use it from now on.",
    addingCta: 'Adding…',
    addAsQaSourceCta: 'Add as Q&A source',
    // Text ONLY — the bold "✓" glyph in front of it is its own `<span className="font-semibold">`
    // in `BotUnanswered.tsx`, kept out of this string so the checkmark stays bold and the
    // sentence stays regular weight, exactly as it rendered before extraction.
    answerAddedNotice: 'Added as a Q&A source — the bot will use it from the next conversation.',
    saveAnswerError: "Couldn't save that answer — please try again.",
  },
  /** Locale-dashboard plan, Task 11 (Batch H): the bot Appearance screen (`BotAppearance.tsx`)
   * and `WidgetPreview.tsx`'s own iframe title (extracted FROM this screen, M5 task-5 — see
   * that file's header comment — so its one string lives in the group that originally owned
   * it, same "extracted-from" precedent `botSettings.embedCopyCta` etc. below follows for
   * `EmbedSnippet.tsx`). The widget language select's OPTION LABELS stay `WIDGET_LOCALE_LABELS`
   * (`@repo/shared-types`) — those are endonyms naming the bot's own answer language, not
   * dashboard chrome, per the plan's scope fence; `autoLanguageLabel` below is the one option
   * that isn't in that map ('auto' has no `WidgetLocale` value) and so is real dashboard copy.
   */
  appearance: {
    pageTitle: 'Appearance',
    pageSubtitle: 'How the widget looks and greets visitors on your site',

    colorLabel: 'Color',
    colorHexAriaLabel: 'Color hex value',
    botNameLabel: 'Bot name',
    botNamePlaceholder: 'Acme Support',
    welcomeMessageLabel: 'Welcome message',
    welcomePlaceholder: 'Hi! How can I help you today?',

    quickQuestionsLabel: 'Quick questions',
    addQuestionCta: 'Add question',
    quickQuestionsHint: "Optional — shown as chips visitors can tap before they've typed anything.",
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Quick question ${n}`,
    quickQuestionPlaceholder: 'What are your business hours?',
    removeCta: 'Remove',

    positionLabel: 'Launcher position',
    positionBottomRight: 'Bottom right',
    positionBottomLeft: 'Bottom left',

    languageLabel: 'Language',
    // 'auto' isn't a `WidgetLocale`, so it isn't in `WIDGET_LOCALE_LABELS` — see
    // `BotAppearance.tsx`'s own doc comment on this label for the length constraint that shaped
    // this wording (must not truncate in the `w-72` select trigger).
    autoLanguageLabel: "Match the visitor's browser",

    badgeLabel: '"Powered by Embent" badge',
    badgeHint: 'Shows a small "Powered by Embent" link in the widget footer.',
    badgeSwitchAriaLabel: 'Show the "Powered by Embent" badge',
    badgeLoadError: "Couldn't load — refresh",
    freeBadgeNote: ({ plans }: { plans: string }) => `Upgrading to ${plans} removes this requirement.`,

    saveCta: 'Save',
    savingCta: 'Saving…',
    savedLabel: 'Saved',

    livePreviewHeading: 'Live preview',
    previewCaption:
      'Only the launcher button and panel render here — the page around it is blank on purpose.',
    // Dev-only (`import.meta.env.DEV`) build instructions, never shown to a bot owner in
    // production — extracted anyway per the plan (rendered English must stay byte-identical
    // regardless of who sees it). The commands/paths themselves (`bun run build`, `apps/widget`,
    // …) are code, not copy, and stay as literals in the component, same idiom as
    // `onboarding.goLivePasteBeforePrefix`/`Suffix` splitting around a `<code>` tag.
    devHintPrefix: "If nothing appears, the widget bundle isn't built locally: run",
    devHintIn: 'in',
    devHintAndCopy: 'and copy',
    devHintTo: 'to',

    // WidgetPreview.tsx
    widgetPreviewTitle: 'Widget preview',
  },
  /** Locale-dashboard plan, Task 11 (Batch H): the bot Settings screen (`BotSettings.tsx`),
   * its Embed snippet card, and the Danger zone. `EmbedSnippet.tsx`'s own copy
   * (`embedCopyCta`/`embedCopiedCard`/`embedCopiedCheck`) lives here rather than under a
   * component-named group of its own because this is the screen it was extracted FROM (see that
   * file's header comment) — the identical copy also renders through the `'hero'`
   * (Overview's `FirstRun.tsx`) and `'overlay'` (onboarding's `GoLiveStep.tsx`) variants, but
   * `EmbedSnippet` resolves it by calling `useT()` internally rather than taking `t` as a prop,
   * so neither of those two already-shipped files needs touching here. The "Platform install
   * guides" link below is NOT its own key — same href, same label as
   * `onboarding.goLiveInstallGuidesLink`, reused verbatim per that key's own established
   * precedent (`overview` group already reuses it for `FirstRun.tsx`'s identical link). */
  botSettings: {
    pageTitle: 'Settings',
    pageSubtitle: 'Allowed domains, lead capture, and how the bot introduces itself',

    leadCaptureOffLabel: 'Off',
    leadCaptureOffBlurb: 'The bot never asks visitors for contact details.',
    leadCaptureOnIntentLabel: 'On intent',
    leadCaptureOnIntentBlurb:
      "The bot asks for an email or phone number only when it can't answer or the visitor wants follow-up.",
    leadCaptureAlwaysLabel: 'Always',
    leadCaptureAlwaysBlurb:
      'The bot asks for contact details once, right after the first exchange in a conversation.',
    leadCapturePreChatLabel: 'Pre-chat form',
    leadCapturePreChatBlurb:
      'Visitors fill a short contact form (name, email, optional phone) before the chat starts. The bot never asks again in-chat.',

    leadLevelSubtleLabel: 'Subtle',
    leadLevelSubtleBlurb: 'Only offers to follow up when the visitor asks to be contacted or wants a quote.',
    leadLevelBalancedLabel: 'Balanced (recommended)',
    leadLevelBalancedBlurb:
      'Offers once when the visitor shows real buying signals — asks how to start, wants a demo, or mentions their own site.',
    leadLevelProactiveLabel: 'Proactive',
    leadLevelProactiveBlurb:
      'Offers whenever the visitor seems to be evaluating, and asks once more later if the first offer went unanswered.',

    domainRequiredError: 'Required — remove the row instead of leaving it blank',
    domainBareHostnameError: 'Bare hostname only, e.g. "example.com" (no scheme or path)',
    domainTooLongError: 'Too long',

    aboutLabel: 'About this bot',
    aboutHint:
      'A short introduction the bot always knows — who it is, who it serves — so questions like ' +
      '"who are you?" get answered instantly without searching.',
    aboutPlaceholder:
      "We're Acme's support assistant, here to help customers set up and troubleshoot Acme Widgets.",

    allowedDomainsLabel: 'Allowed domains',
    addDomainCta: 'Add domain',
    allowedDomainsHint:
      "Sites allowed to embed this bot's widget. Leave empty to allow every site — recommended " +
      'to lock this down to your own domain(s) before going live.',
    domainAriaLabel: ({ n }: { n: number }) => `Allowed domain ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Remove',

    leadCaptureLabel: 'Lead capture',
    leadLevelLabel: 'How proactive?',

    saveCta: 'Save',
    savingCta: 'Saving…',
    savedLabel: 'Saved',

    embedSnippetHeading: 'Embed snippet',
    embedPastePrefix: 'Paste this before the closing',
    embedPasteSuffix: 'tag on your site.',

    // EmbedSnippet.tsx — shared verbatim across its 'card' (here)/'hero' (FirstRun.tsx)/
    // 'overlay' (GoLiveStep.tsx) variants; resolves via its own `useT()` call.
    embedCopyCta: 'Copy',
    embedCopiedCard: 'Copied!',
    embedCopiedCheck: '✓ Copied',

    dangerZoneHeading: 'Danger zone',
    dangerZoneDescription:
      'Deletes the bot, its sources, conversations, leads, and its search index. This cannot be undone.',
    deleteBotCta: 'Delete this bot',
    deleteBotDialogTitle: ({ name }: { name: string }) => `Delete "${name}"?`,
    dangerZoneDialogDescriptionPrefix:
      'Deletes the bot, its sources, conversations, leads, and its search index. This cannot be undone. Type ',
    dangerZoneDialogDescriptionSuffix: ' to confirm.',
    confirmBotNameAriaLabel: 'Confirm bot name',
    cancelCta: 'Cancel',
    deletingCta: 'Deleting…',
  },
  /** Locale-dashboard plan, Task 11 (Batch H): `NotificationsCard.tsx`, mounted on
   * `BotSettings.tsx` above the Danger zone. Resolves its own copy via `useT()` internally
   * (same idiom as `chat-bubbles.tsx`'s `AssistantBubble`) rather than taking `t` as a prop —
   * `NotificationsCard.test.tsx` renders it standalone with no `LocaleProvider`, and `useT()`'s
   * documented no-provider default (real English) is exactly what keeps that test green
   * untouched. */
  notifications: {
    heading: 'Notifications',
    subheading: "Emails go to each person's account address. Playground activity never triggers emails.",
    noMembers: 'No members.',
    saveError: "Couldn't save that setting. Try again.",

    roleOwner: 'Owner',
    roleAdmin: 'Admin',
    roleMember: 'Member',
    cadenceOff: 'Off',
    cadenceDaily: 'Daily',
    cadenceWeekly: 'Weekly',

    colMember: 'Member',
    colNewLead: 'New lead',
    colUnansweredDigest: 'Unanswered digest',
    colCrawlFailed: 'Crawl failed',
    colWeeklySummary: 'Weekly summary',

    newLeadAriaLabel: ({ email }: { email: string }) => `New lead emails for ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `Unanswered digest emails for ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `Crawl failed emails for ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `Weekly summary emails for ${email}`,
  },
  /** Locale-dashboard plan, Task 11 (Batch H): the in-app Playground screen (`Playground.tsx`)
   * — dashboard chrome only. The simulated widget's own strings (`SEARCHING_STATUS` via
   * `resolvePlaygroundLocale`, and every bubble rendered through the shared
   * `components/chat-bubbles.tsx`, already extracted with its own `useT()` in Task 10) follow
   * the BOT's language and are untouched here, per the plan's scope fence. */
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: "Chat with your bot the way a visitor would — nothing here reaches your site.",
    retrievalDebugLabel: 'Retrieval debug',
    hideDebugCta: 'Hide debug',
    newSessionCta: 'New session',
    emptyStateText: 'Send a message to test how this bot answers, grounded in its sources.',
    inputPlaceholder: 'Ask this bot a question…',
    stopCta: 'Stop',
    sendCta: 'Send',
    debugEmptyText: 'Ask a question to see what it retrieves',
    genericError: 'Something went wrong.',
  },
  /** Locale-dashboard plan, Task 12 (Batch I): the Billing screen (`Billing.tsx`) — one of the
   * THREE `PLAN_LIMITS` render sites (CLAUDE.md, "Pricing is rendered in THREE places"). Every
   * cap/price still comes straight off `PLAN_LIMITS` in the component — only the surrounding
   * unit phrase ("X messages / mo", "X pages") and every other sentence on the screen become
   * keys here. `botsUnit`/`seatsUnit`/`messagesPerMonth`/`pagesUnit` back `PlanCard`'s feature
   * list; `pagesUnit` is reused verbatim for `UsageBar`'s own "pages" upgrade hint (identical
   * phrase, no "/mo" suffix on either side) while `messagesUnit` is a SEPARATE key from
   * `messagesPerMonth` because that hint line never carries "/mo" — two renderings of the same
   * number that must not accidentally start rendering the same text just because they agree
   * today. `unlimitedWord` ('unlimited', `renewalLine`'s `fmt` helper) and `unlimitedParens`
   * ('(unlimited)', `UsageBar`'s own value line) are likewise two separate keys for two
   * differently-punctuated renderings of the same idea.
   *
   * `capsSuffix` composes the current-plan card's "X bots · Y messages · Z pages" clause;
   * `freeForever`/`renewsOn` wrap it with the two lines above it can appear under
   * (`renewsOn` here is a DIFFERENT string from `shell.renewsOn` — that one has no caps
   * clause — kept as its own key in this group rather than reused, same policy as every other
   * batch's coincidentally-matching short phrases). `internalRenewalFallback` is `renewalLine`'s
   * own internal-workspace branch, which today's call site never actually reaches (the caller
   * already checks `summary.internal` before invoking the function) — extracted anyway since
   * dead-but-present code must still render correctly if it were ever reached, same standard
   * `appearance`'s dev-only build-instructions paragraph was held to. `internalWorkspaceInline`
   * and `internalWorkspaceHeading` are the two DIFFERENT sentences that actually render for an
   * internal workspace today (one inline under the plan name, one as the standalone card's own
   * heading) — three near-identical "internal workspace, no billing" sentences, three keys,
   * because nothing ties them together in the source.
   *
   * `planChangeConfirm.*` backs `lib/billing-copy.ts`'s `planChangeConfirmCopy`, which now takes
   * `t: Catalog` as a required field on its params object (no default — matches every other
   * `t: Catalog` parameter in this plan) rather than resolving its own copy, since it is a pure
   * function, not a component. `billing-copy.test.ts` passes `t: en` and its money-safety
   * regexes are UNCHANGED — they assert against the rendered English these keys still produce,
   * not against source code. `verbUpgrade`/`verbSwitch` are separate keys from `upgradeCta`
   * (the plain button label) even though both render "Upgrade" today — one is a sentence
   * fragment inside a dynamically-composed title, the other a standalone clickable action;
   * matching text, different role, per the `auth` group's stated policy. */
  billing: {
    pageTitle: 'Billing',
    openingCta: 'Opening…',
    manageBillingCta: 'Manage billing',

    memberGateTitle: 'Billing is managed by your workspace owner or an admin.',
    memberGateDescription: "Ask an owner or admin to make changes to this workspace's plan.",

    nowOnPlan: ({ name }: { name: string }) => `You're now on the ${name} plan.`,
    finishingUp: "We're finishing this up — refresh in a moment if your plan doesn't update.",
    confirmingWithPolar: 'Change received — confirming with Polar…',

    internalWorkspaceLabel: 'Internal workspace',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / month`,
    internalWorkspaceInline: 'No limits, billing does not apply.',
    internalWorkspaceHeading: 'Internal workspace — no limits, billing does not apply.',
    internalWorkspaceBody:
      'Every plan cap is bypassed and there is nothing to upgrade or check out here. Usage is ' +
      'still recorded above for visibility, but it is never pushed to Polar — this workspace ' +
      'has no billing identity.',

    unlimitedWord: 'unlimited',
    unlimitedParens: '(unlimited)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    // `renewalLine`'s `fmt`/`capsSuffix` composition (current-plan card subline).
    internalRenewalFallback: 'Internal workspace · no limits · billing does not apply',
    capsSuffix: ({
      bots,
      botsCount,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} bot${botsCount === 1 ? '' : 's'} · ${messages} messages · ${pages} pages`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Free forever · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) =>
      `Renews ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Cancels at period end',
    planChangesPausedNotice: 'Plan changes are paused until the current period ends.',
    planChangesPausedReason:
      'This subscription is set to cancel at the end of the period — resume it first to change plans.',

    redirectingCta: 'Redirecting…',
    switchingCta: 'Switching…',
    upgradeToCta: ({ name }: { name: string }) => `Upgrade to ${name}`,

    messagesThisMonthLabel: 'Messages this month',
    pagesIndexedLabel: 'Pages indexed',
    messageNoun: 'message',
    pageNoun: 'page',
    // `UsageBar`'s own upgrade-hint unit — no "/mo" suffix, unlike `messagesPerMonth` above,
    // which is why this is a SEPARATE key rather than a reuse.
    messagesUnit: ({ n }: { n: string }) => `${n} messages`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) =>
      `${pct}% of your ${noun} limit`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) =>
      ` — ${name} raises it to ${unit}.`,
    limitReached: ' reached.',

    cancelSubscriptionCta: 'Cancel subscription',
    cancelingCta: 'Canceling…',
    cancelDialogTitle: 'Cancel subscription?',
    yourCurrentPlanFallback: 'your current plan',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Your bots keep working on ${planName} through the end of this billing period${
        renewsDate ? ` (${renewsDate})` : ''
      } — then the workspace drops to Free. This never happens immediately.`,
    keepSubscriptionCta: 'Keep subscription',

    changePlanFallbackTitle: 'Change plan?',
    keepCurrentPlanCta: 'Keep current plan',
    confirmFallbackCta: 'Confirm',

    checkoutErrorFallback: 'Something went wrong starting checkout.',
    portalErrorFallback: 'Something went wrong opening billing management.',
    cancelErrorFallback: 'Something went wrong canceling your subscription.',

    // PlanCard
    currentPlanBadge: 'Current plan',
    botsUnit: ({ n }: { n: number }) => `${n} bot${n === 1 ? '' : 's'}`,
    seatsUnit: ({ n }: { n: number }) => `${n} seat${n === 1 ? '' : 's'}`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} messages / mo`,
    pagesUnit: ({ n }: { n: string }) => `${n} pages`,
    badgeRequiredText: '"Powered by Embent" badge',
    badgeOptionalText: '"Powered by Embent" badge optional',
    upgradeCta: 'Upgrade',
    switchToCta: ({ name }: { name: string }) => `Switch to ${name}`,

    flatPricingNote:
      'Flat pricing, no per-seat math, no surprise overages — we warn you at 80% and pause new ' +
      'answers only past 110%, and never cut a conversation off mid-answer.',

    // lib/billing-copy.ts — planChangeConfirmCopy
    planChangeConfirm: {
      verbUpgrade: 'Upgrade',
      verbSwitch: 'Switch',
      title: ({ verb, name }: { verb: string; name: string }) => `${verb} to ${name}?`,
      confirmNow: ({ verb }: { verb: string }) => `${verb} now`,
      continueToPolarCta: 'Continue to Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `You'll be taken to Polar to start a ${name} subscription ($${price}/mo) — Polar charges ` +
        'you there to activate it.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `This switches your plan immediately — your bots start using ${name}'s limits ` +
        `($${price}/mo) right away. Polar won't charge you today: it applies the prorated ` +
        'difference to your next invoice instead.',
    },
  },
  /** Locale-dashboard plan, Task 12 (Batch I): the Workspace settings screen
   * (`WorkspaceSettings.tsx`) — rename form and the logo upload/crop flow
   * (`LogoCropDialog.tsx`'s own strings live here too, same "screen that owns it" precedent
   * `botSettings.embedCopyCta` set). `pageTitle` is ONE key reused across all three render
   * branches (loading/error/normal) — literally the same `<PageHeader>` on the same screen, not
   * three independent renderings. */
  workspace: {
    pageTitle: 'Workspace settings',
    loadErrorMessage: "Couldn't load this workspace. Reload the page to try again.",
    notAllowedTitle: 'Only owners and admins can change workspace settings.',
    notAllowedDescription: 'Ask an owner or admin of this workspace to make changes.',

    nameLabel: 'Workspace name',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} characters. Shown to everyone in this workspace and in invite emails.`,

    uploadLogoCta: 'Upload logo',
    removeLogoCta: 'Remove',
    logoHint: 'Square works best. PNG, JPEG, WebP or GIF, up to 2 MB.',
    logoNotConfiguredError: "Logo uploads aren't configured on this environment.",
    uploadLogoErrorFallback: "Couldn't upload the logo. Try again.",
    removeLogoErrorFallback: "Couldn't remove the logo. Try again.",

    saveNameErrorFallback: "Couldn't save the name. Try again.",
    saveCta: 'Save',
    savedLabel: 'Saved',

    // LogoCropDialog.tsx
    cropDialogTitle: 'Crop your logo',
    cropDialogDescription: 'Drag to reposition, zoom to fit. Saved as a 256×256 square.',
    zoomLabel: 'Zoom',
    cropErrorFallback: "Couldn't crop this image. Try a different file.",
    cancelCta: 'Cancel',
    useThisLogoCta: 'Use this logo',
  },
  /** Locale-dashboard plan, Task 12 (Batch I): the Members screen (`Members.tsx`) — the members
   * table, the pending-invites table, the invite dialog, and the Remove/Leave-workspace confirm
   * dialog. `roleOwner`/`roleAdmin`/`roleMember` mint their own keys rather than reusing
   * `notifications.roleOwner`/etc. — same word, different screen, per the `auth` group's stated
   * policy. `roleOptionMember`/`roleOptionAdmin` ARE shared across the two role `<Select>`s on
   * THIS screen (the per-row role-change dropdown and the invite dialog's own role picker) —
   * literally the same assignable-role list rendered twice for the same purpose on the same
   * page, unlike the members-table vs. invites-table column headers below (which look alike but
   * are two different tables, so they mint separate `colX`/`inviteColX` keys). `inviteMemberCta`
   * is reused for both the header action button and the dialog it opens (same word naming the
   * same flow, same idiom `conversations.deleteAllCta` set for a trigger + its own dialog).
   * `leaveWorkspaceCta`/`removeCta` are likewise reused between the per-row action and the
   * confirm dialog's own primary button — same flow, same button word, same screen. */
  members: {
    pageTitle: 'Members',
    membersOnlyTitle: 'Members-only area',
    membersOnlyDescription: 'Ask your workspace owner or an admin to manage members.',
    unlimitedCapWord: 'Unlimited',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used} of ${cap} seats used${pending > 0 ? ` (${pending} invited)` : ''}`,
    inviteMemberCta: 'Invite member',

    roleOwner: 'Owner',
    roleAdmin: 'Admin',
    roleMember: 'Member',
    roleOptionMember: 'Member',
    roleOptionAdmin: 'Admin',

    colName: 'Name',
    colEmail: 'Email',
    colRole: 'Role',
    leaveWorkspaceCta: 'Leave workspace',
    removeCta: 'Remove',

    pendingInvitesHeading: 'Pending invites',
    noPendingInvites: 'No pending invites.',
    inviteColEmail: 'Email',
    inviteColRole: 'Role',
    inviteColExpires: 'Expires',
    revokingCta: 'Revoking…',
    revokeCta: 'Revoke',

    cancelCta: 'Cancel',
    removingCta: 'Removing…',
    leaveDialogTitle: 'Leave this workspace?',
    removeDialogTitle: ({ name }: { name: string }) => `Remove ${name}?`,
    leaveDialogDescription: "You'll lose access immediately — an owner or admin can invite you back later.",
    removeDialogDescription: ({ name }: { name: string }) => `${name} loses access to this workspace immediately.`,

    emailLabel: 'Email',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Role',
    inviteErrorFallback: 'Something went wrong sending this invite.',
    sendingInviteCta: 'Sending…',
    sendInviteCta: 'Send invite',

    roleChangeErrorFallback: 'Could not change this role.',
    removeErrorFallback: 'Could not remove this member.',
    revokeErrorFallback: 'Could not revoke this invite.',
  },
  /** Locale-dashboard plan, Task 12 (Batch I): the Account screen (`Account.tsx`) — Profile
   * (display name + read-only email/sign-in method) and Password sections. `savingCta`
   * ('Saving…') is ONE key shared by both sections' own Save buttons — same screen, same
   * pending-state word, same idiom `conversations`'s shared `cancelCta`/`deletingCta` already
   * set for two dialogs on one screen — unlike the cross-SCREEN "Saving…" pairs elsewhere
   * (`appearance.savingCta` vs `botSettings.savingCta`), which stay separate because they are
   * different pages. */
  account: {
    pageTitle: 'Account',
    loadSignInMethodsError: 'Could not load sign-in methods',
    emailPasswordLabel: 'Email & password',
    googleLabel: 'Google',

    savingCta: 'Saving…',

    profileHeading: 'Profile',
    displayNameLabel: 'Display name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email',
    saveNameErrorFallback: 'Something went wrong saving your name.',
    saveCta: 'Save',
    savedLabel: 'Saved',

    passwordHeading: 'Password',
    passwordMismatchError: 'New password and confirmation do not match.',
    currentPasswordLabel: 'Current password',
    newPasswordLabel: 'New password',
    confirmNewPasswordLabel: 'Confirm new password',
    changePasswordErrorFallback: 'Something went wrong changing your password.',
    changePasswordCta: 'Change password',
    passwordChangedLabel: 'Password changed',
  },
  /** Locale-dashboard plan, Task 12 (Batch I): `lib/admin-impersonate.ts`'s four static-fallback
   * `Error` messages — NOT one of the four screen groups this batch's brief names (`billing`/
   * `workspace`/`members`/`account`), because this lib file has no screen of its own; it is
   * called only from `AdminWorkspaces.tsx`/`AdminBotDetail.tsx` (Task 13's own files). Each
   * function below now takes `t: Catalog` as a required parameter (no default, matching every
   * other `t: Catalog` parameter in this plan) since it is a plain async helper, not a component,
   * and cannot call `useT()` itself — its two call sites are updated here to the minimum needed
   * to keep `check-types` green (threading `t` through, `AdminBotDetail.tsx` already had
   * `useT()` for other reasons; `AdminWorkspaces.tsx` gained its own `useT()` call). Neither
   * page's OWN copy (headings, table cells, the catch-block's own generic fallback strings) is
   * touched here — that stays Task 13's job. `error.message` itself (the better-auth server
   * payload) is a passthrough and stays OUT of the catalog, same as every other batch; only the
   * friendly literal this app substitutes when there is no server message gets a key. */
  adminImpersonate: {
    impersonateFailedFallback: 'Could not impersonate this user.',
    lookupFailedFallback: 'Could not look up this user.',
    userNotFound: 'User account not found.',
    noOwnerToView: 'This workspace has no owner to view as.',
  },
  /** Locale-dashboard plan, Task 13 (Batch J): the admin ops screens — Workspaces (+ its Crawl
   * Signal panel and the workspace detail page), Bots (+ bot detail), Jobs (+ job detail), and
   * the shared `AdminPagination.tsx` control. Admin screens get NO special treatment (plan
   * decision, task brief): extracted exactly like every customer-facing screen.
   *
   * Grouped under one `admin` object with `pagination`/`workspaces`/`bots`/`jobs` sub-groups,
   * matching the task brief's own grouping. `job.kind` and every raw status/role/type value that
   * mirrors an API or better-auth enum (`bot.status`, `source.status`, `source.type`,
   * `job.status`, `owner.role`, `subscription.status`) stays untouched as data, per the brief's
   * instruction — only the rendered LABEL wrapping such a value becomes a key (`jobs.tabLabel`
   * for the status-filter tabs, `workspaces.memberRoleLabel` for `MEMBER_ROLE_LABEL`).
   *
   * `err.message`/`ApiError.message` passthroughs (every mutation's error branch — including
   * `admin-impersonate.ts`'s own thrown `Error`s bubbling through `AdminWorkspaces.tsx`'s and
   * `AdminBotDetail.tsx`'s own catch blocks) stay OUT of the catalog; only the friendly fallback
   * literal this app substitutes when there is no server message gets a key, same as every other
   * batch.
   *
   * `jobs`'s Retry/Cancel micro-copy (`retryCta`/`retryingCta`/`cancelCta`/`cancellingCta`/
   * `cancelJobCta`/`keepJobCta`/`cancelDialogTitle`/`retryJobErrorFallback`/
   * `cancelJobErrorFallback`) is ONE set of keys shared by both `AdminJobs.tsx` (the list's
   * inline actions) and `AdminJobDetail.tsx` (the identical two actions on the identical job) —
   * the same action on the same entity, triggered from two screens, same idiom
   * `conversations.cancelCta`/`deletingCta` already set for two dialogs on one screen. The two
   * cancel dialogs' DESCRIPTION text differs (the list's carries an extra caveat sentence the
   * detail page's doesn't), so those stay two separate keys
   * (`cancelDialogDescriptionListRow`/`cancelDialogDescriptionDetailPage`). Every other coincidentally-matching
   * word across `workspaces`/`bots`/`jobs` (e.g. "Created", "Status") mints its own key per
   * sub-group and per role (table column vs. facts-card label vs. section heading), same policy
   * the `auth` group states explicitly. */
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Page ${page} of ${totalPages} · ${totalCount} total`,
      prevCta: 'Prev',
      nextCta: 'Next',
    },
    /** AdminWorkspaces.tsx (list + `CrawlSignalPanel`) and AdminWorkspaceDetail.tsx. */
    workspaces: {
      // CrawlSignalPanel
      crawlSignalRenderedLabel: 'Depends on rendered-mode crawling',
      crawlSignalSourcesAcrossBots: ({
        sourceCount,
        botCountLabel,
        botCount,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) =>
        `source${sourceCount === 1 ? '' : 's'} across ${botCountLabel} bot${botCount === 1 ? '' : 's'}`,
      crawlSignalRenderedFootnote:
        'How much of the fleet relies on Cloudflare Browser Rendering — capacity we get on the Workers Free plan today, not Paid.',
      crawlSignalCapacityLabel: 'Hit our own rendering capacity limit (7d)',
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `source${n === 1 ? '' : 's'}`,
      crawlSignalCapacityFootnote:
        "Distinct sources, our own capacity only — never a customer's plan cap. A killed process or a reaped stale job leaves no record — this is a floor. Climbing steadily means it's time for Workers Paid ($5/mo).",

      // AdminWorkspaces.tsx
      emptyText: 'No workspaces yet.',
      internalBadge: 'INTERNAL',
      cancelsAtPeriodEndHint: 'cancels at period end',
      colName: 'Name',
      colOwner: 'Owner',
      colPlan: 'Plan',
      colBots: 'Bots',
      colMembers: 'Members',
      colMsgsPerMo: 'Msgs / mo',
      colPages: 'Pages',
      colCreated: 'Created',
      colActions: 'Actions',
      noOwnerTitle: 'This workspace has no owner',
      viewAsCta: 'View as',
      switchingCta: 'Switching…',
      viewAsErrorFallback: 'Failed',

      // AdminWorkspaceDetail.tsx
      backToWorkspaces: '← Back to Workspaces',
      ownerLabel: 'Owner: ',
      bannedBadge: 'banned',
      createdLabel: ({ date }: { date: string }) => `Created ${date}`,
      usageHeading: 'Usage',
      messagesThisMonthLabel: 'Messages this month',
      pagesIndexedLabel: 'Pages indexed',
      botsLabel: 'Bots',
      internalUsageNote: 'Internal workspace — no caps apply; usage is still recorded above.',
      botsHeading: 'Bots',
      noBotsYet: 'No bots yet.',
      colBotName: 'Name',
      colPublicId: 'Public ID',
      colStatus: 'Status',
      colBotCreated: 'Created',
      membersHeading: 'Members',
      noMembersYet: 'No members.',
      colEmail: 'Email',
      colMemberName: 'Name',
      colRole: 'Role',
      colJoined: 'Joined',
      /** `MEMBER_ROLE_LABEL` (`MemberRoleChip`'s own vocabulary) — a rendered label mapping, not
       * a raw enum passthrough, so it becomes keys per the brief's own rule. */
      memberRoleLabel: {
        owner: 'Owner',
        admin: 'Admin',
        member: 'Member',
      },
      subscriptionHeading: 'Subscription',
      statusLabel: 'Status: ',
      periodEndLabel: 'Period end: ',
      cancelsAtPeriodEndLabel: 'Cancels at period end: ',
      yesLabel: 'Yes',
      noLabel: 'No',
      polarSubscriptionIdLabel: 'Polar subscription ID: ',
      manageInPolarLink: 'Manage in Polar ↗',
      noSubscriptionText: 'No subscription — this workspace is on the free plan.',
      dangerZoneHeading: 'Danger zone',
      internalToggleLabel: 'Internal workspace',
      internalToggleHint: 'No caps, no billing — usage is still recorded but never pushed to Polar.',
      banOwnerLabel: 'Ban owner',
      banOwnerHint: 'Banned users cannot sign in. The widget/public API are unaffected.',
      unbanCta: 'Unban',
      unbanningCta: 'Unbanning…',
      banCta: 'Ban',
      demoteBeforeBanTitle: 'Demote this admin to user before banning them.',
      roleLabel: 'Role',
      roleHint: 'Admins can access every internal admin screen.',
      cannotChangeOwnRoleTitle: 'You cannot change your own role.',
      cancelCta: 'Cancel',
      savingCta: 'Saving…',
      confirmCta: 'Confirm',
      markInternalDialogTitle: 'Mark this workspace internal?',
      removeInternalDialogTitle: 'Remove internal status?',
      markInternalDialogDescription:
        'This workspace will bypass every plan cap (bots, messages, pages) and will never be billed. Usage is still recorded for visibility, but the nightly Polar usage push skips it — there is no billing identity to push to.',
      removeInternalDialogDescription:
        'This workspace goes back to normal plan enforcement and billing (if it has an active subscription).',
      banDialogTitle: ({ email }: { email: string | undefined }) => `Ban ${email}?`,
      banDialogDescription: 'This immediately signs them out and prevents any future sign-in until unbanned.',
      banReasonLabel: 'Reason (optional)',
      banReasonPlaceholder: 'Why is this account being banned?',
      banningCta: 'Banning…',
      banUserCta: 'Ban user',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `Change role to ${role}?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} will gain access to every internal admin screen.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} will lose access to internal admin screens.`,
      updateWorkspaceErrorFallback: 'Could not update this workspace.',
      banUserErrorFallback: 'Could not ban this user.',
      unbanUserErrorFallback: 'Could not unban this user.',
      changeRoleErrorFallback: 'Could not change this user’s role.',
    },
    /** AdminBots.tsx (list) and AdminBotDetail.tsx. */
    bots: {
      // AdminBots.tsx
      colName: 'Name',
      colPublicId: 'Public ID',
      colWorkspace: 'Workspace',
      colStatus: 'Status',
      colSources: 'Sources',
      colLastCrawl: 'Last crawl',
      colInstanceId: 'Instance ID',
      noBotsYet: 'No bots yet.',

      // AdminBotDetail.tsx
      backToBots: '← Back to Bots',
      workspaceLabel: 'Workspace: ',
      instanceLabel: ({ id }: { id: string }) => `Instance: ${id}`,
      createdLabel: ({ date }: { date: string }) => `Created ${date}`,
      openOwnerViewCta: "Open owner's view",
      openingCta: 'Opening…',
      openOwnerViewErrorFallback: 'Could not open the owner’s view.',
      sourcesHeading: 'Sources',
      noSourcesYet: 'No sources yet.',
      colUrl: 'URL',
      colType: 'Type',
      colSourceStatus: 'Status',
      colPages: 'Pages',
      colLastCrawled: 'Last crawled',
      colError: 'Error',
      recentJobsHeading: 'Recent jobs',
      noJobsLinked: 'No jobs linked to this bot yet.',
      colKind: 'Kind',
      colJobStatus: 'Status',
      colAttempts: 'Attempts',
      colUpdated: 'Updated',
      colPayload: 'Payload',
      recentJobsFootnote:
        "Recent jobs are matched best-effort via each job's payload — a job that doesn't carry this bot's id or one of its source ids won't show up here even if it touched this bot indirectly.",
    },
    /** AdminJobs.tsx (list) and AdminJobDetail.tsx. */
    jobs: {
      tabLabel: {
        all: 'All',
        running: 'Running',
        failed: 'Failed',
      },
      noFailedJobs: 'No failed jobs — the queue is healthy.',
      noJobsToShow: 'No jobs to show.',
      colKind: 'Kind',
      colStatus: 'Status',
      colPayload: 'Payload',
      colAttempts: 'Attempts',
      colCreated: 'Created',
      colUpdated: 'Updated',
      colActions: 'Actions',
      // Shared between AdminJobs.tsx's inline row actions and AdminJobDetail.tsx's own —
      // see this group's own header comment for why.
      retryCta: 'Retry',
      retryingCta: 'Retrying…',
      cancelCta: 'Cancel',
      cancellingCta: 'Cancelling…',
      cancelJobCta: 'Cancel job',
      keepJobCta: 'Keep job',
      cancelDialogTitle: 'Cancel this job?',
      retryJobErrorFallback: 'Could not retry this job.',
      cancelJobErrorFallback: 'Could not cancel this job.',
      cancelDialogDescriptionListRow:
        "This deletes the pending job row — it will never run. Only pending jobs can be cancelled this way; a running job's handler may already be mid-flight against it.",

      // AdminJobDetail.tsx
      backToJobs: '← Back to Jobs',
      jobIdLabel: 'Job ID',
      attemptsLabel: 'Attempts',
      createdLabel: 'Created',
      updatedLabel: 'Updated',
      runAfterLabel: 'Run after',
      errorHeading: 'Error',
      payloadHeading: 'Payload',
      cancelDialogDescriptionDetailPage: 'This deletes the pending job row — it will never run.',
    },
    /** AdminPosts.tsx — the blog posts list. Mints its own `scheduledLabel`/status vocabulary
     * rather than reusing `postEditor`'s, same policy as every other pair of admin screens in
     * this file (a matching English string still gets its own key when the role differs). The
     * `PostStatusChip`'s non-scheduled branch passes the raw `visibility` value itself
     * (`'live'`/`'draft'`) straight through as `label` — an enum passthrough, not a rendered
     * LABEL mapping, so per this file's own rule it stays data and is not keyed here; only the
     * literal "scheduled · " template text below is copy. */
    posts: {
      description:
        'Blog posts, drafts and published. Editing here goes live on embent.com within a minute of publishing.',
      newPostCta: 'New post',
      emptyText: 'No posts yet — create the first one.',
      colTitle: 'Title',
      colStatus: 'Status',
      colAuthor: 'Author',
      colPublished: 'Published',
      colUpdated: 'Updated',
      scheduledLabel: ({ date }: { date: string }) => `scheduled · ${date}`,
    },
    /** AdminPostEditor.tsx — editor CHROME only (field labels, placeholders, publish/schedule
     * states, validation and autosave/upload messages, the "Writing guide" panel and its AI
     * briefing prompt template). Blog post CONTENT — title, slug, excerpt, tags, cover URL, body
     * markdown, author name/role/bio/persona: anything typed into a post or author field — is
     * authored data and never a key; only the surrounding UI text below is. Same enum-passthrough
     * treatment as `posts.scheduledLabel`'s sibling: the `StatusChip`'s `label={visibility}` is
     * data, not keyed. */
    postEditor: {
      // Header
      backToPosts: '← Posts',
      newPostTitle: 'New post',
      editPostTitleFallback: 'Edit post',

      // Post fields
      titleLabel: 'Title',
      titlePlaceholder: 'How Embent answers support questions',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Excerpt',
      excerptPlaceholder:
        'One or two sentences — shown on /blog and used as the meta description.',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Comma-separated.',
      coverUrlLabel: 'Cover image URL',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      // Author picker
      authorLabel: 'Author',
      editAuthorCta: 'Edit author',
      newAuthorCta: '+ New author',
      authorSelectPlaceholder: 'Choose an author',
      noAuthorsYet: 'No authors yet — add one with "+ New author" above.',

      // Publish / visibility
      visibilityScheduledText: ({ date }: { date: string }) => `Goes live ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Published ${date}`,
      visibilityDraftText: 'Not published yet',
      publishDateLabel: 'Publish date',
      publishDateHintPrefix: 'Times are in ',
      publishDateHintSuffix:
        " — this browser's clock. Leave empty to go live the moment you publish. Set a future time and the post stays hidden until then — it disappears from the blog, its tag counts, the sitemap and the RSS feed until the clock catches up. Nothing has to be running for it to appear.",

      // Body + preview
      bodyLabel: 'Body (Markdown)',
      livePreviewLabel: 'Live preview',

      // Validation
      /** Rendered LABEL mapping for the `missing` field-name tokens `PostForm` pushes onto its
       * own array (`'title'`/`'slug'`/`'excerpt'`/`'body'`/`'author'`) — those tokens double as
       * internal identifiers AND the literal words joined into the sentence below, so per this
       * file's own "a rendered LABEL, not a raw passthrough, becomes a key" rule they're keyed
       * here rather than left as data. */
      missingFieldLabel: {
        title: 'title',
        slug: 'slug',
        excerpt: 'excerpt',
        body: 'body',
        author: 'author',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Before saving, add: ${fields}.`,

      // Save / publish actions
      savingCta: 'Saving…',
      saveChangesCta: 'Save changes',
      saveDraftCta: 'Save draft',
      unpublishingCta: 'Unpublishing…',
      schedulingCta: 'Scheduling…',
      publishingCta: 'Publishing…',
      unpublishCta: 'Unpublish',
      scheduleCta: 'Schedule',
      publishCta: 'Publish',
      savedFlash: 'Saved',

      // Preview pane
      renderingPreview: 'Rendering…',
      previewFailedPrefix: 'Preview failed to load: ',

      // Markdown editor / image uploads
      uploadFailedFallback: 'Upload failed',
      bodyPlaceholder: 'Write in Markdown… paste or drag an image in to upload it inline.',
      uploadingText: 'Uploading…',
      imageUploadFailedPrefix: 'Image upload failed: ',

      // Delete dialog
      deleteCta: 'Delete',
      deleteDialogTitle: ({ title }: { title: string }) => `Delete "${title}"?`,
      deleteDialogDescriptionPrefix: 'Permanently deletes this post. This cannot be undone. Type',
      deleteDialogDescriptionSuffix: ' to confirm.',
      confirmPostTitleAriaLabel: 'Confirm post title',
      cancelCta: 'Cancel',
      deletingCta: 'Deleting…',
      deletePostCta: 'Delete post',

      // Author dialog
      editAuthorDialogTitle: ({ name }: { name: string }) => `Edit ${name}`,
      newAuthorDialogTitle: 'New author',
      authorDialogDescription: 'Real bylines only — no fabricated identities.',
      authorNameLabel: 'Name',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Role',
      authorRolePlaceholder: 'Founder',
      authorAvatarLabel: 'Avatar URL',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Bio',
      authorPersonaLabel: 'Persona (internal)',
      authorPersonaPlaceholder:
        'Writes in short, direct sentences. Skeptical of hype. Prefers a concrete example over an adjective.',
      authorPersonaHint:
        "A briefing note for drafting in this byline's voice — never published, never shown to readers.",
      authorSavingCta: 'Saving…',
      authorAddingCta: 'Adding…',
      saveAuthorCta: 'Save author',
      addAuthorCta: 'Add author',

      // Writing guide panel (spec §G)
      writingGuideHeading: 'Writing guide',
      writingGuideSubtitle: 'Markdown support, images, and an AI briefing prompt',
      supportedMarkdownHeading: 'Supported Markdown',
      mdListHeadings: 'Headings, levels 1 through 6',
      mdListLists: 'Bulleted and numbered lists',
      mdListLinksPrefix: 'Links — ',
      mdListLinksSuffix: ', http(s) and mailto only',
      mdListImages:
        'Images anywhere in the body, including mid-article — paste or drag one in (see below)',
      mdListCode: 'Inline and fenced code blocks, syntax highlighted',
      mdListTables: 'Tables, GitHub-flavored, with column alignment',
      mdListBlockquotes: 'Blockquotes',
      strippedHeading: 'What gets stripped',
      strippedPrefix: 'Everything else. Raw HTML tags, and ',
      strippedSuffix:
        " content specifically, never survive — the body is sanitized against a fixed allow-list (headings, lists, links, images, code, tables, blockquotes, plus the paragraph/emphasis they're built from) before it renders anywhere, including here in preview.",
      imagesHeading: 'Images',
      imagesPrefix: 'Paste or drag an image into the body — it uploads to cdn.embent.com and inserts',
      imagesSuffix:
        ' at the caret, wherever your cursor is, so it can land mid-paragraph. The cover image above is a separate field — it never comes from something dropped in the body.',
      copiedCta: 'Copied!',
      copyAiPromptCta: 'Copy AI prompt',
      copiedToClipboard: 'Copied to clipboard.',
      clipboardUnavailable:
        "Clipboard isn't available in this browser — select and copy the prompt below.",
      assemblesPrefix: 'Assembles house style, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) =>
        `${authorName}'s persona`,
      assemblesNoAuthorClause: 'a note that no author is selected',
      assemblesSuffix:
        ', the Markdown conventions above, and a requested structure — paste it into an external model, then paste the draft back into the body.',

      /** `buildAiPrompt`'s two persona-clause branches and the fixed template around them
       * (`{ personaSection }`) — split so the interpolation point lands exactly where the
       * original template put it, byte-for-byte. `authorName` here is already resolved to its
       * own fallback word by the caller (`aiPromptAuthorFallbackLower`/`Upper`) before being
       * passed in, matching the original `authorName ?? 'this author'` / `?? 'This author'`
       * logic. */
      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Write in ${authorName}'s voice:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} has no persona set — write in a plain, direct voice.`,
      aiPromptAuthorFallbackLower: 'this author',
      aiPromptAuthorFallbackUpper: 'This author',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `You are drafting a blog post for Embent (embent.com), an AI agent platform for websites.

House style:
- Direct and concrete. No hype, no marketing fluff.
- Short paragraphs (2-4 sentences).
- Never invent statistics or customer names. If you don't have a real number or a real customer, don't make one up.

Author voice:
${personaSection}

Markdown conventions (this is ALL that will render — everything else is stripped):
- Headings (#, ##, ###), bulleted and numbered lists, blockquotes.
- Links as [text](url) — http(s) and mailto only.
- Images anywhere in the body, including mid-article, as ![alt](url).
- Inline and fenced code blocks.
- Tables (GitHub-flavored).
- Raw HTML and <script>/<style> tags are stripped entirely — write plain Markdown only, no HTML.

Requested structure:
1. Title
2. One-paragraph hook
3. 4-7 sections, each with an h2 (##) heading
4. A short closing

Write the full draft now as Markdown.`,
    },
    /** AdminFeedback.tsx. `FILTER_LABEL`/`TYPE_LABEL` are rendered LABEL mappings (same
     * treatment as `admin.jobs.tabLabel`), not raw enum passthroughs — `TYPE_VARIANT`'s Badge
     * `variant` values and the wire `handled=all|false` query param stay data. The mailto
     * subject's `row.type` stays a raw passthrough too (unchanged from the original — the
     * subject line never went through `TYPE_LABEL` either). */
    feedback: {
      filterLabel: {
        unhandled: 'Unhandled',
        all: 'All',
      },
      typeLabel: {
        bug: 'Bug',
        feature: 'Feature',
        question: 'Question',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) =>
        `Re: [Embent feedback ${id}] ${type}`,
      replyCta: 'Reply',
      colDate: 'Date',
      colWorkspace: 'Workspace',
      colUser: 'User',
      colType: 'Type',
      colMessage: 'Message',
      colActions: 'Actions',
      emptyUnhandled: 'No unhandled feedback — inbox zero.',
      emptyAll: 'No feedback yet.',
      updateFeedbackErrorFallback: 'Could not update this feedback.',
      reopeningCta: 'Reopening…',
      markingCta: 'Marking…',
      reopenCta: 'Reopen',
      markHandledCta: 'Mark handled',
      noBotFallback: 'no bot',
      noUaFallback: 'no UA',
    },
    /** AdminPlayground.tsx. `demoUnit`/`messageUnit` back `BarStrip`'s pluralized tooltip/aria
     * label (`count === 1 ? unit : \`${unit}s\``) — the English "s" plural branching stays in the
     * calling code per this catalog's own header comment (Vietnamese has no plural inflection),
     * only the bare singular unit word is a key. `DemoStatus` values rendered in the status-
     * breakdown chips and the recent-domains table (`row.status`) are raw enum passthroughs, not
     * keyed, same treatment every other batch gives `bot.status`/`source.status`/`job.status`. */
    playground: {
      demosAllTimeLabel: 'Demos all time',
      demos30dLabel: 'Demos · 30d',
      messagesAllTimeLabel: 'Messages all time',
      signedUpAllTimeLabel: 'Signed up',
      signedUp30dLabel: 'Signed up · 30d',
      leadsAllTimeLabel: 'Leads captured',
      leads30dLabel: 'Leads · 30d',
      demosPerDayLabel: 'Demos per day',
      messagesPerDayLabel: 'Messages per day',
      // Arg functions, not bare nouns: English pluralizes ("3 demos") where Vietnamese
      // repeats the classifier ("3 demo") — a `${unit}s` append in the component rendered
      // broken VI ("tin nhắns"), found by the Task-15 translation.
      demoUnit: ({ count }: { count: number }): string => (count === 1 ? 'demo' : 'demos'),
      messageUnit: ({ count }: { count: number }): string => (count === 1 ? 'message' : 'messages'),
      noDemos30d: 'No demos in the last 30 days.',
      statusBreakdownHeading: 'Status breakdown (30d)',
      recentDomainsHeading: 'Recent domains',
      noDemosYet: 'No demos yet.',
      colHostname: 'Hostname',
      colStatus: 'Status',
      colPages: 'Pages',
      colMessages: 'Messages',
      colAttempts: 'Attempts',
      colSignedUp: 'Signed up',
      colLeads: 'Leads',
      colCreated: 'Created',
      signedUpAriaLabel: 'signed up',
      notSignedUpAriaLabel: 'not signed up',
      noEmailFallback: '(no email)',
    },
  },
};

export type Catalog = typeof en;
